// src/components/groups/GroupJoinLinkCard.jsx
// The group admin's shared join link (Roadmap #54, Invitations Build 2 §5, Phase 3), in AdminGroup's
// invite column. One live link per group: create it with an expiry (1–90 days, default 14), copy
// it, replace it (the old one stops at once), switch it off; see when it was made, when it expires
// and how many joined through it. Anyone holding it joins with no approval, private groups too
// (Michael, 1 Oct 2026) — the card says so plainly, because the smart link beside it does not
// behave that way (Advisor A9).

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { groupService } from '../../data/dataService';
import StandardButton from '../core/StandardButton';
import ConfirmationModal from '../core/ConfirmationModal';
import { tokens } from '../../styles/designTokens';

export const JOIN_LINK_DAY_CHOICES = [1, 7, 14, 30, 60, 90];
export const JOIN_LINK_DEFAULT_DAYS = 14;

const GroupJoinLinkCard = ({ groupId }) => {
  const { t, formattingLocale } = useTranslation('groups');
  const [link, setLink] = useState(undefined); // undefined = loading; null = none; else the link
  const [days, setDays] = useState(JOIN_LINK_DEFAULT_DAYS);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);         // an action did not succeed
  const [loadFailed, setLoadFailed] = useState(false); // the link could not be read
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'replace' | 'off'

  // A failed read is its own state, never "no link yet": Create switches off any live link, so
  // offering it after a read error could silently kill a link already shared (Advisor, pre-commit A2).
  const load = useCallback(async () => {
    const result = await groupService.getGroupJoinLink(groupId);
    if (result?.error) { setLoadFailed(true); return; }
    setLoadFailed(false);
    setLink(result);
  }, [groupId]);

  useEffect(() => { if (groupId) load(); }, [groupId, load]);

  const run = async (action) => {
    setConfirm(null);
    setWorking(true);
    setFailed(false);
    const ok = action === 'off'
      ? await groupService.revokeGroupJoinLink(groupId)
      : (await groupService.createGroupJoinLink(groupId, days)).success;
    if (!ok) setFailed(true);
    await load();
    setWorking(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying join link:', err?.name);
    }
  };

  const date = (iso) => new Date(iso).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' });

  const daySelect = (
    <label className="flex items-center gap-2 text-xs text-gray-600">
      {t('join_link_admin_expires_after')}
      <select
        value={days}
        onChange={(e) => setDays(Number(e.target.value))}
        disabled={working}
        className="border border-gray-300 rounded px-2 py-1 text-xs"
      >
        {JOIN_LINK_DAY_CHOICES.map(n => (
          <option key={n} value={n}>{t(n === 1 ? 'join_link_admin_days_one' : 'join_link_admin_days_many', { count: n })}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="bg-white rounded-lg p-4 shadow-md mb-4">
      <h3 className="font-semibold text-gray-800 mb-1 text-sm">{t('join_link_admin_title')}</h3>
      <p className="text-xs text-gray-500 mb-3">{t('join_link_admin_description')}</p>

      {loadFailed ? (
        <div className="space-y-2">
          <p className="text-xs" style={{ color: tokens.colors.destructive }}>{t('join_link_admin_load_error')}</p>
          <StandardButton variant="ghost" iconName="RefreshCw" label={t('email_action_try_again')} onClick={load} disabled={working} />
        </div>
      ) : link === undefined ? (
        <p className="text-xs text-gray-500">{t('join_link_admin_loading')}</p>
      ) : link ? (
        <div className="space-y-2">
          <div className="bg-gray-50 rounded p-2 flex items-center gap-2">
            <span className="flex-1 text-xs text-gray-700 truncate" title={link.link}>{link.link}</span>
            {!link.isExpired && (
              <StandardButton
                variant="inline"
                iconName={copied ? 'Check' : 'Copy'}
                label={t('join_link_admin_copy')}
                onClick={copy}
                title={t('join_link_admin_copy')}
              />
            )}
          </div>
          <div className="text-xs text-gray-600 space-y-0.5">
            <div>{t('join_link_admin_created', { date: date(link.createdAt) })}</div>
            <div style={link.isExpired ? { color: tokens.colors.destructive } : undefined}>
              {t(link.isExpired ? 'join_link_admin_expired' : 'join_link_admin_expires', { date: date(link.expiresAt) })}
            </div>
            <div>{t(link.joins === 1 ? 'join_link_admin_joins_one' : 'join_link_admin_joins_many', { count: link.joins })}</div>
          </div>
          {daySelect}
          <div className="flex flex-wrap gap-2 pt-1">
            <StandardButton
              variant="action"
              iconName="RefreshCw"
              label={t(link.isExpired ? 'join_link_admin_create_new' : 'join_link_admin_replace')}
              onClick={() => (link.isExpired ? run('create') : setConfirm('replace'))}
              disabled={working}
            />
            {!link.isExpired && (
              <StandardButton
                variant="ghost"
                iconName="Ban"
                label={t('join_link_admin_switch_off')}
                onClick={() => setConfirm('off')}
                disabled={working}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">{t('join_link_admin_none')}</p>
          {daySelect}
          <StandardButton
            variant="action"
            iconName="Plus"
            label={t('join_link_admin_create')}
            onClick={() => run('create')}
            disabled={working}
          />
        </div>
      )}

      {failed && <p className="text-xs mt-2" style={{ color: tokens.colors.destructive }}>{t('join_link_admin_error')}</p>}

      <ConfirmationModal
        visible={confirm !== null}
        title={t(confirm === 'off' ? 'join_link_admin_switch_off' : 'join_link_admin_replace')}
        message={t(confirm === 'off' ? 'join_link_admin_switch_off_confirm' : 'join_link_admin_replace_confirm')}
        okLabel={t(confirm === 'off' ? 'join_link_admin_switch_off' : 'join_link_admin_replace')}
        cancelLabel={t('common:cancel')}
        danger
        onOk={() => run(confirm === 'off' ? 'off' : 'create')}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default GroupJoinLinkCard;
